import React from 'react';
import { FeedbackPatterns, ComponentProps, FeedbackSource } from '../types.js';

/**
 * PatternAnalysis - Platform Admin
 * 
 * UPDATED: Dec 22, 2025 - Changed to handle FeedbackPatterns object structure
 * Backend returns patterns as object with nested arrays, not array of PatternInsight
 * 
 * Following 00-READ-THIS-FIRST:
 * - SINGLE SOURCE OF TRUTH: Recovered original, not duplicated
 * - NO FUNCTIONAL DUPLICATES: This is the only PatternAnalysis
 * - API CONTRACT: Matches backend feedback_routes.py:609-651
 */

interface PatternAnalysisProps extends ComponentProps {
  patterns: FeedbackPatterns;
  source: FeedbackSource;
}

export const PatternAnalysis: React.FC<PatternAnalysisProps> = ({ patterns, source }) => {
  // Backend returns object with nested arrays, display common_corrections
  const corrections = patterns.common_corrections || [];

  return (
    <div className="bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Pattern Analysis</h3>
      
      {corrections.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>No patterns detected yet.</p>
          <p className="text-sm mt-2">Collect analyst feedback to detect correction patterns.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Common Corrections</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-gray-700">
                    <th className="pb-3">Original Label</th>
                    <th className="pb-3">Corrected To</th>
                    <th className="pb-3">Frequency</th>
                    <th className="pb-3">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {corrections.map((correction, idx) => (
                    <tr key={idx} className="border-b border-gray-700/50">
                      <td className="py-3 font-medium">{correction.original_label}</td>
                      <td className="py-3">{correction.corrected_label}</td>
                      <td className="py-3">{correction.frequency}</td>
                      <td className="py-3">{correction.percentage.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {patterns.recommendations && patterns.recommendations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Recommendations</h4>
              <ul className="text-sm text-gray-400 space-y-1">
                {patterns.recommendations.map((rec, idx) => (
                  <li key={idx}>• {rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
